import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../../../../api/client";
import Tabs from "../../components/Tabs";
import Toast from "../../components/Toast";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";
import { usePermission } from "../../../../hooks/usePermission";
import useAuth from "../../../../hooks/useAuth";
import { PERMISSIONS } from "../../../../constants/permissions";
import MachineModal from "./MachineModal";
import MaintenanceModal from "./MaintenanceModal";
import ScheduleModal from "./ScheduleModal";
import WarrantyModal from "./WarrantyModal";
import QrLabelModal from "./QrLabelModal";
import { ReportBreakdownModal, ResolveBreakdownModal } from "./BreakdownModals";
import { BreakdownTable, MachineThumb, Pill, RecordTable, ScheduleTable } from "./AssetManagement";
import {
  BREAKDOWN_STATUS,
  CRITICALITY,
  MACHINE_STATUS,
  WARRANTY_STATE,
  WARRANTY_TYPES,
  errorMessage,
  fileUrl,
  formatDate,
  formatDuration,
  formatHours,
  formatMoney,
  formatPercent,
  formatWhen,
} from "./assetUtils";
import "./Assets.css";

const P = PERMISSIONS;

const LOG_TEXT = {
  reported: "Reported",
  note: "Added",
  acknowledged: "Acknowledged",
  resolved: "Resolved",
  reminder: "Reminder sent",
  escalated: "Escalated",
};

function BreakdownDetail({ breakdown, people, canAct, onAcknowledge, onResolve, onClose }) {
  const name = (id) => people.find((p) => p.id === id)?.name || "Someone";
  return (
    <div className="am-panel" style={{ borderColor: breakdown.status === "resolved" ? "#e2e8f0" : "#fca5a5" }}>
      <div className="am-panel-head">
        <h2 className="am-panel-title">
          {breakdown.label} <Pill meta={BREAKDOWN_STATUS[breakdown.status]} />
        </h2>
        <button type="button" className="am-close" onClick={onClose} aria-label="Close breakdown">✕</button>
      </div>
      <p style={{ margin: "0 0 10px", whiteSpace: "pre-wrap" }}>{breakdown.description}</p>
      <div className="am-specs" style={{ marginTop: 0 }}>
        <div><dt>Stopped</dt><dd>{formatWhen(breakdown.startedAt)}</dd></div>
        <div><dt>Downtime</dt><dd>{formatDuration(breakdown.downtimeMinutes)}{breakdown.status !== "resolved" ? " and counting" : ""}</dd></div>
        <div><dt>Response time</dt><dd>{breakdown.responseMinutes != null ? formatDuration(breakdown.responseMinutes) : "Not yet acknowledged"}</dd></div>
        <div><dt>Reported via</dt><dd>{breakdown.reportedVia === "web" ? "App" : breakdown.reportedVia === "whatsapp" ? "WhatsApp" : "Telegram"}</dd></div>
      </div>
      {(breakdown.voicePath || breakdown.photoPath) && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
          {breakdown.voicePath && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio controls src={fileUrl(breakdown.voicePath)} style={{ maxWidth: "100%" }} />
          )}
          {breakdown.photoPath && (
            <a href={fileUrl(breakdown.photoPath)} target="_blank" rel="noreferrer">
              <img src={fileUrl(breakdown.photoPath)} alt="Breakdown" style={{ height: 96, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            </a>
          )}
        </div>
      )}
      <ol className="am-timeline" style={{ marginTop: 14 }}>
        {(breakdown.log || []).map((entry, i) => (
          <li key={i} className={entry.type}>
            <strong>{LOG_TEXT[entry.type] || entry.type}</strong>
            {/* Reminders and escalations are sent by the system, not a person. */}
            {entry.byId ? ` by ${name(entry.byId)}` : ""}
            {entry.via && entry.via !== "web" ? ` on ${entry.via}` : ""} · <span className="am-muted">{formatWhen(entry.at)}</span>
            {entry.text && <div style={{ whiteSpace: "pre-wrap" }}>{entry.text}</div>}
          </li>
        ))}
      </ol>
      {canAct && breakdown.status !== "resolved" && (
        <div className="am-actions">
          {breakdown.status === "open" && (
            <button type="button" className="am-btn am-btn-ghost" onClick={() => onAcknowledge(breakdown)}>Acknowledge</button>
          )}
          <button type="button" className="am-btn am-btn-primary" onClick={() => onResolve(breakdown)}>Resolve — machine running</button>
        </div>
      )}
    </div>
  );
}

export default function MachineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  const canManage = usePermission(P.MANUFACTURING_ASSET_MANAGE);
  const hasMaintain = usePermission([P.MANUFACTURING_ASSET_MAINTAIN, P.MANUFACTURING_ASSET_MANAGE]);
  const canReport = usePermission([P.MANUFACTURING_ASSET_BREAKDOWN_REPORT, P.MANUFACTURING_ASSET_MAINTAIN, P.MANUFACTURING_ASSET_MANAGE]);
  const canSeeMaintenance = usePermission([P.MANUFACTURING_ASSET_VIEW, P.MANUFACTURING_ASSET_MAINTAIN, P.MANUFACTURING_ASSET_MANAGE]);

  const [machine, setMachine] = useState(null);
  const [machines, setMachines] = useState([]);
  const [people, setPeople] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("breakdowns");
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const photoInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, all, ppl, cl] = await Promise.all([
        api.get(`/manufacturing/assets/machines/${id}`),
        api.get("/manufacturing/assets/machines"),
        api.get("/manufacturing/assets/people"),
        canSeeMaintenance ? api.get("/manufacturing/assets/checklists") : Promise.resolve(null),
      ]);
      setMachine(m.data.data);
      setMachines(all.data.data);
      setPeople(ppl.data.data);
      if (cl) setChecklists(cl.data.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load this machine."));
    }
  }, [id, canSeeMaintenance]);

  useEffect(() => { load(); }, [load]);

  // Arriving from a QR sticker opens the breakdown form straight away.
  useEffect(() => {
    if (machine && params.get("report") === "1" && canReport) {
      setModal({ kind: "report" });
      params.delete("report");
      setParams(params, { replace: true });
    }
  }, [machine, params, setParams, canReport]);

  // The machine's own technician can act on it without the maintain permission,
  // the same rule the server applies.
  const canMaintain = hasMaintain || (machine?.technician && machine.technician.id === user?.id);

  const selectedId = params.get("breakdown");
  const selected = machine?.breakdowns.find((b) => b.id === selectedId) || null;
  const openBreakdown = machine?.breakdowns.find((b) => b.status !== "resolved");

  const selectBreakdown = (b) => {
    setParams(b ? { breakdown: b.id } : {}, { replace: true });
    if (b) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const done = (msg) => () => {
    setModal(null);
    setToast({ msg, type: "success" });
    load();
  };

  const run = async (fn, okMsg, failMsg) => {
    try {
      await fn();
      setToast({ msg: okMsg, type: "success" });
      load();
    } catch (err) {
      setToast({ msg: errorMessage(err, failMsg), type: "error" });
    }
  };

  const acknowledge = (b) =>
    run(() => api.post(`/manufacturing/assets/breakdowns/${b.id}/acknowledge`), `${b.label} acknowledged`, "Couldn't acknowledge.");

  const uploadPhoto = (file) => {
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    run(() => api.post(`/manufacturing/assets/machines/${id}/image`, data), "Photo updated", "Couldn't upload the photo.");
  };

  if (error) {
    return (
      <div className="am-page">
        <Link className="back-link" to="/manufacturing/assets?tab=machines">← Back to Asset Management</Link>
        <div className="am-empty"><strong>Couldn't open this machine</strong>{error}</div>
      </div>
    );
  }
  if (!machine) return <div className="am-page"><div className="am-empty">Loading…</div></div>;

  const rel = machine.reliability;
  const tabs = [
    { key: "breakdowns", label: `Breakdowns (${machine.breakdowns.length})` },
    ...(canSeeMaintenance
      ? [
        { key: "pm", label: `Preventive maintenance (${machine.schedules.length})` },
        { key: "history", label: `Maintenance history (${machine.records.length})` },
      ]
      : []),
    { key: "warranty", label: `Warranty & AMC (${machine.warranties.length})` },
  ];

  return (
    <div className="am-page">
      <Link className="back-link" to="/manufacturing/assets?tab=machines">← Back to Asset Management</Link>

      {openBreakdown && !selected && (
        <div className="am-down-banner">
          <span>
            <strong>Down since {formatWhen(openBreakdown.startedAt)}</strong> · {openBreakdown.label} · {formatDuration(openBreakdown.downtimeMinutes)} of downtime so far
          </span>
          <button type="button" className="am-btn am-btn-ghost am-btn-sm" onClick={() => selectBreakdown(openBreakdown)}>View breakdown</button>
        </div>
      )}

      {selected && (
        <BreakdownDetail
          breakdown={selected}
          people={people}
          canAct={canMaintain}
          onAcknowledge={acknowledge}
          onResolve={(b) => setModal({ kind: "resolve", breakdown: b })}
          onClose={() => selectBreakdown(null)}
        />
      )}

      <div className="am-detail-head">
        <div>
          <div className="am-detail-photo"><MachineThumb machine={machine} /></div>
          {canManage && (
            <div className="am-detail-photo-actions">
              <button type="button" className="am-link-btn" onClick={() => photoInput.current?.click()}>
                {machine.imagePath ? "Change photo" : "Add photo"}
              </button>
              {machine.imagePath && (
                <button
                  type="button"
                  className="am-link-btn danger"
                  onClick={() => run(() => api.delete(`/manufacturing/assets/machines/${id}/image`), "Photo removed", "Couldn't remove the photo.")}
                >
                  Remove
                </button>
              )}
              <input
                ref={photoInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }}
              />
            </div>
          )}
        </div>
        <div>
          <div className="am-detail-title-row">
            <div>
              <h1 className="am-title">{machine.name}</h1>
              <p className="am-sub" style={{ marginTop: 4 }}>
                <span className="am-code">{machine.code}</span> · {machine.type}
              </p>
              <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Pill meta={MACHINE_STATUS[machine.status]} />
                <Pill meta={WARRANTY_STATE[machine.warrantyState]} />
              </div>
            </div>
            <div className="am-actions">
              {canReport && machine.status !== "retired" && (
                <button type="button" className="am-btn am-btn-danger" onClick={() => setModal({ kind: "report" })}>🚨 Report breakdown</button>
              )}
              {canMaintain && (
                <button type="button" className="am-btn am-btn-ghost" onClick={() => setModal({ kind: "maintenance" })}>Log maintenance</button>
              )}
              <button type="button" className="am-btn am-btn-ghost" onClick={() => setModal({ kind: "qr" })}>QR label</button>
              {canManage && (
                <>
                  <button type="button" className="am-btn am-btn-ghost" onClick={() => setModal({ kind: "edit" })}>Edit</button>
                  <button type="button" className="am-btn am-btn-ghost" style={{ color: "#dc2626" }} onClick={() => setConfirm("delete")}>Delete</button>
                </>
              )}
            </div>
          </div>
          <dl className="am-specs">
            <div><dt>Serial number</dt><dd>{machine.serialNumber || "—"}</dd></div>
            <div><dt>Make / model</dt><dd>{[machine.make, machine.model].filter(Boolean).join(" ") || "—"}</dd></div>
            <div><dt>Location</dt><dd>{machine.location || "—"}</dd></div>
            <div><dt>Technician</dt><dd>{machine.technician?.name || "Fallback technicians"}</dd></div>
            <div><dt>Criticality</dt><dd>{CRITICALITY.find((c) => c.value === machine.criticality)?.label.split(" —")[0] || machine.criticality}</dd></div>
            <div><dt>Running hours</dt><dd>{machine.runningHours != null ? `${machine.runningHours} h` : "—"}</dd></div>
            <div><dt>Purchased</dt><dd>{formatDate(machine.purchaseDate)}{machine.purchaseCost != null ? ` · ${formatMoney(machine.purchaseCost)}` : ""}</dd></div>
            <div><dt>Installed</dt><dd>{formatDate(machine.installDate)}</dd></div>
          </dl>
          {machine.notes && <p className="am-muted" style={{ whiteSpace: "pre-wrap", marginTop: 12 }}>{machine.notes}</p>}
        </div>
      </div>

      {rel && canSeeMaintenance && (
        <div className="am-stats">
          <div className="am-stat">
            <p className="am-stat-label">Availability · 90 days</p>
            <p className="am-stat-value">{formatPercent(rel.availability)}</p>
          </div>
          <div className="am-stat">
            <p className="am-stat-label">Downtime</p>
            <p className="am-stat-value">{formatHours(rel.downtimeHours)}</p>
            <p className="am-stat-note">{rel.failures} breakdown{rel.failures === 1 ? "" : "s"}</p>
          </div>
          <div className="am-stat">
            <p className="am-stat-label">MTTR</p>
            <p className="am-stat-value">{formatHours(rel.mttrHours)}</p>
          </div>
          <div className="am-stat">
            <p className="am-stat-label">MTBF</p>
            <p className="am-stat-value">{formatHours(rel.mtbfHours)}</p>
          </div>
        </div>
      )}

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === "breakdowns" && (
        machine.breakdowns.length === 0 ? (
          <div className="am-empty"><strong>No breakdowns</strong>This machine hasn't been reported down.</div>
        ) : (
          <BreakdownTable
            rows={machine.breakdowns}
            hideMachine
            onOpen={selectBreakdown}
            canMaintain={canMaintain}
            onAcknowledge={acknowledge}
            onResolve={(b) => setModal({ kind: "resolve", breakdown: b })}
          />
        )
      )}

      {tab === "pm" && (
        <div className="am-panel">
          <div className="am-panel-head">
            <h2 className="am-panel-title">Schedules</h2>
            {canManage && (
              <button type="button" className="am-btn am-btn-ghost am-btn-sm" onClick={() => setModal({ kind: "schedule" })}>+ New schedule</button>
            )}
          </div>
          {machine.schedules.length === 0 ? (
            <p className="am-muted" style={{ margin: 0 }}>No preventive maintenance is scheduled for this machine.</p>
          ) : (
            <ScheduleTable
              rows={machine.schedules}
              hideMachine
              canMaintain={canMaintain}
              canManage={canManage}
              onComplete={(s) => setModal({ kind: "maintenance", schedule: s })}
              onEdit={(s) => setModal({ kind: "schedule", schedule: s })}
              onDelete={(s) => setConfirm({ schedule: s })}
            />
          )}
        </div>
      )}

      {tab === "history" && (
        machine.records.length === 0 ? (
          <div className="am-empty"><strong>No maintenance logged</strong>Completed PMs, repairs and inspections appear here.</div>
        ) : (
          <RecordTable rows={machine.records} hideMachine />
        )
      )}

      {tab === "warranty" && (
        <div className="am-panel">
          <div className="am-panel-head">
            <h2 className="am-panel-title">Warranties and maintenance contracts</h2>
            {canManage && (
              <button type="button" className="am-btn am-btn-ghost am-btn-sm" onClick={() => setModal({ kind: "warranty" })}>+ Add</button>
            )}
          </div>
          {machine.warranties.length === 0 ? (
            <p className="am-muted" style={{ margin: 0 }}>No warranty recorded.</p>
          ) : (
            <div className="am-table-wrap">
              <table className="am-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Provider</th>
                    <th>Period</th>
                    <th>Covers</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {[...machine.warranties].sort((a, b) => new Date(b.endDate) - new Date(a.endDate)).map((w) => (
                    <tr key={w.id}>
                      <td>{WARRANTY_TYPES.find((t) => t.value === w.warrantyType)?.label || w.warrantyType}</td>
                      <td>
                        {w.provider}
                        {w.contact && <div className="am-muted">{w.contact}</div>}
                      </td>
                      <td>{formatDate(w.startDate)} – {formatDate(w.endDate)}{w.cost != null && <div className="am-muted">{formatMoney(w.cost)}</div>}</td>
                      <td><div className="am-clip">{w.coverage || "—"}</div></td>
                      <td><Pill meta={WARRANTY_STATE[w.state]} /></td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {w.documentPath && (
                          <a className="am-link-btn" href={fileUrl(w.documentPath)} target="_blank" rel="noreferrer">Document ↗</a>
                        )}
                        {canManage && (
                          <>
                            {w.documentPath && " · "}
                            <button type="button" className="am-link-btn" onClick={() => setModal({ kind: "warranty", warranty: w })}>Edit</button>
                            {" · "}
                            <button type="button" className="am-link-btn danger" onClick={() => setConfirm({ warranty: w })}>Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modal?.kind === "edit" && (
        <MachineModal machine={machine} people={people} onClose={() => setModal(null)} onSaved={done("Machine updated")} />
      )}
      {modal?.kind === "report" && (
        <ReportBreakdownModal
          machines={machines}
          machineId={machine.id}
          onClose={() => setModal(null)}
          onSaved={(res) => {
            done(res.duplicate ? `Added to ${res.breakdown.label}` : res.notified ? `${res.breakdown.label} reported — the technician has been alerted on chat` : `${res.breakdown.label} reported — but no technician could be alerted on chat`)();
            selectBreakdown(res.breakdown);
          }}
        />
      )}
      {modal?.kind === "resolve" && (
        <ResolveBreakdownModal breakdown={modal.breakdown} onClose={() => setModal(null)} onSaved={done(`${modal.breakdown.label} resolved`)} />
      )}
      {modal?.kind === "maintenance" && (
        <MaintenanceModal
          machines={machines}
          machineId={machine.id}
          schedule={modal.schedule}
          checklists={checklists}
          people={people}
          onClose={() => setModal(null)}
          onSaved={done(modal.schedule ? "Preventive maintenance completed" : "Maintenance logged")}
        />
      )}
      {modal?.kind === "schedule" && (
        <ScheduleModal
          schedule={modal.schedule}
          machines={machines}
          machineId={machine.id}
          checklists={checklists}
          people={people}
          onClose={() => setModal(null)}
          onSaved={done(modal.schedule ? "Schedule updated" : "Schedule created")}
        />
      )}
      {modal?.kind === "warranty" && (
        <WarrantyModal machineId={machine.id} warranty={modal.warranty} onClose={() => setModal(null)} onSaved={done(modal.warranty ? "Warranty updated" : "Warranty added")} />
      )}
      {modal?.kind === "qr" && <QrLabelModal machine={machine} onClose={() => setModal(null)} />}

      {confirm === "delete" && (
        <ConfirmDialog
          title={`Delete ${machine.name}?`}
          message="Its warranties, maintenance history, schedules and breakdowns are deleted with it. To keep the history, set its status to Retired instead."
          confirmLabel="Delete machine"
          confirmClass="btn-danger"
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            try {
              await api.delete(`/manufacturing/assets/machines/${machine.id}`);
              navigate("/manufacturing/assets?tab=machines");
            } catch (err) {
              setConfirm(null);
              setToast({ msg: errorMessage(err, "Couldn't delete the machine."), type: "error" });
            }
          }}
        />
      )}
      {confirm?.schedule && (
        <ConfirmDialog
          title={`Delete "${confirm.schedule.title}"?`}
          message="Reminders stop. Maintenance already logged against it is kept."
          confirmLabel="Delete schedule"
          confirmClass="btn-danger"
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            run(() => api.delete(`/manufacturing/assets/pm-schedules/${confirm.schedule.id}`), "Schedule deleted", "Couldn't delete the schedule.");
          }}
        />
      )}
      {confirm?.warranty && (
        <ConfirmDialog
          title="Delete this warranty?"
          message={`${confirm.warranty.provider}, ${formatDate(confirm.warranty.startDate)} – ${formatDate(confirm.warranty.endDate)}.`}
          confirmLabel="Delete"
          confirmClass="btn-danger"
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            setConfirm(null);
            run(() => api.delete(`/manufacturing/assets/warranties/${confirm.warranty.id}`), "Warranty deleted", "Couldn't delete the warranty.");
          }}
        />
      )}
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
